import { UNLIMITED_CAPACITY } from "@salon/types";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isInfinityLimit } from "../types";

interface InfinityToggleInputProps {
  valueFieldName: string;
  placeholder?: string;
  min?: number;
  max?: number;
  infinityLabel?: string;
  customLabel?: string;
  saving?: boolean;
  className?: string;
}

export function InfinityToggleInput({
  valueFieldName,
  placeholder = "0",
  min = 1,
  max = 1000,
  infinityLabel,
  customLabel,
  saving = false,
  className = "",
}: InfinityToggleInputProps) {
  const { t } = useTranslation("settings");
  const { control, setValue } = useFormContext();
  const currentValue = useWatch({ control, name: valueFieldName });

  const isInfinity = isInfinityLimit(currentValue);

  const defaultInfinityLabel = infinityLabel || t("capacity.infinity");
  const defaultCustomLabel = customLabel || t("capacity.custom");
  // Clearing the input falls back to `min`, so only the range can fail
  const rangeMessage = t("capacity.limitRange", { min, max, infinity: defaultInfinityLabel });

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Select
        value={isInfinity ? "infinity" : "custom"}
        onValueChange={(value) => {
          if (value === "infinity") {
            // Set to 999 for infinity
            setValue(valueFieldName, UNLIMITED_CAPACITY, { shouldDirty: true });
          } else {
            // Set to a reasonable default when switching to custom
            setValue(valueFieldName, min, { shouldDirty: true });
          }
        }}
        disabled={saving}
      >
        <SelectTrigger className="w-[140px] h-10">
          <SelectValue>{isInfinity ? defaultInfinityLabel : defaultCustomLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="infinity">{defaultInfinityLabel}</SelectItem>
          <SelectItem value="custom">{defaultCustomLabel}</SelectItem>
        </SelectContent>
      </Select>

      {!isInfinity && (
        <FormField
          control={control}
          name={valueFieldName}
          rules={{
            min: { value: min, message: rangeMessage },
            max: { value: max, message: rangeMessage },
          }}
          render={({ field }) => (
            <FormItem className="flex flex-col items-end space-y-1">
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === "" ? min : Number(e.target.value))
                  }
                  disabled={saving}
                  className="w-24 h-10 text-center font-semibold"
                  min={min}
                  max={max}
                  placeholder={placeholder}
                />
              </FormControl>
              <FormMessage className="max-w-56 text-right" />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
